import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { num, loanLegalFee, loanStampDuty, valuationFee, monthlyInstallment, DISBURSEMENT_ESTIMATE } from '../lib/loanCalc';

// Refinance RESULT — /tools/refinance/result, arrives with { f } in router state.
// New loan = market × margin. Costs: loan legal fee (SRO 2023 on the new
// facility), disbursement estimate, loan stamp duty (0.5%), lock-in penalty
// (% of outstanding), valuation fee (LPPEH scale on market value, min RM400).

const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RefinanceResultPage() {
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const { state } = useLocation();
  const [infoOpen, setInfoOpen] = useState(false);
  const f = state?.f;

  const r = useMemo(() => {
    if (!f) return null;
    const outstanding = num(f.outstanding);
    const market = num(f.marketPrice);
    const newLoan = market * (num(f.marginPct) / 100);
    const newRepayment = monthlyInstallment(newLoan, f.ratePct, f.years);
    const costs = {
      loanLegal: newLoan > 0 ? loanLegalFee(newLoan) : 0,
      disb: newLoan > 0 ? DISBURSEMENT_ESTIMATE : 0,
      loanStamp: loanStampDuty(newLoan),
      lockIn: outstanding * (num(f.lockInPct) / 100),
      valuation: valuationFee(market),
    };
    return {
      outstanding,
      newLoan,
      currentRepayment: num(f.currentRepayment),
      newRepayment,
      monthlyDiff: num(f.currentRepayment) - newRepayment,
      cashOut: Math.max(0, newLoan - outstanding),
      costs,
      costTotal: Object.values(costs).reduce((s, v) => s + v, 0),
    };
  }, [f]);

  if (!f || !r) {
    nav('/tools/refinance', { replace: true });
    return null;
  }

  const share = async () => {
    const lines = [
      'CID Refinance estimate',
      `Outstanding: RM ${fmt2(r.outstanding)} → New loan: RM ${fmt2(r.newLoan)} (${num(f.marginPct)}% of RM ${fmt2(num(f.marketPrice))})`,
      `Repayment: RM ${fmt2(r.currentRepayment)} → RM ${fmt2(r.newRepayment)} (${r.monthlyDiff >= 0 ? 'saves' : 'adds'} RM ${fmt2(Math.abs(r.monthlyDiff))}/month)`,
      `Cash out: RM ${fmt2(r.cashOut)}`,
      `Refinancing costs: RM ${fmt2(r.costTotal)}`,
    ].join('\n');
    try {
      if (navigator.share) { await navigator.share({ text: lines }); return; }
      await navigator.clipboard.writeText(lines);
      message.success('Result copied to clipboard');
    } catch (_) { /* cancelled */ }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title="Refinance"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div onClick={() => setInfoOpen(true)} style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><circle cx="12" cy="7.6" r="0.4" fill="currentColor" /></svg>
          </div>
          <div onClick={share} style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="2.6" /><circle cx="6" cy="12" r="2.6" /><circle cx="18" cy="19" r="2.6" /><line x1="8.3" y1="10.8" x2="15.7" y2="6.2" /><line x1="8.3" y1="13.2" x2="15.7" y2="17.8" /></svg>
          </div>
          </div>
        }
      />

      <div style={{ display: 'flex', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ flex: 1, padding: '14px 16px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Cash Out</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>RM{fmt2(r.cashOut)}</div>
        </div>
        <div style={{ width: 1, background: 'var(--border)', margin: '10px 0' }} />
        <div style={{ flex: 1, padding: '14px 16px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Monthly Difference</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, color: r.monthlyDiff >= 0 ? 'var(--ok)' : 'var(--bad)' }}>
            RM{fmt2(Math.abs(r.monthlyDiff))} ({r.monthlyDiff >= 0 ? 'less' : 'more'})
          </div>
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Block title="SUMMARY">
          <Row label="Current Outstanding Amount" value={fmt2(r.outstanding)} />
          <Row label="New Loan Amount" value={fmt2(r.newLoan)} />
          <Row label="Current Monthly Repayment" value={fmt2(r.currentRepayment)} />
          <Row label="New Monthly Repayment" value={fmt2(r.newRepayment)} strong />
        </Block>

        <Block title="NEW LOAN COSTS">
          <Row label="Loan Legal Fees" value={fmt2(r.costs.loanLegal)} />
          <Row label="Disbursement & Misc Charges*" value={fmt2(r.costs.disb)} />
          <Row label="Loan Stamp Duty" value={fmt2(r.costs.loanStamp)} />
          <Row label="Lock-in Penalty" value={fmt2(r.costs.lockIn)} />
          <Row label="Valuation Fee**" value={fmt2(r.costs.valuation)} />
          <Row label="Total (RM)" value={fmt2(r.costTotal)} strong />
        </Block>
      </div>

      <InfoNotes
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Refinance notes"
        pages={[
          { title: 'Costs', body: '* Estimated RM2,500 for solicitor disbursements & misc charges.\n\n** Valuation fee per the LPPEH (Board of Valuers) scale on the estimated market price, minimum RM400 \u2014 excludes SST and the valuer\u2019s disbursements.' },
          { title: 'Scales', body: 'Loan legal fees per SRO 2023 (minimum RM500); loan stamp duty 0.5% of the new facility; lock-in penalty applied on the current outstanding amount.\n\nSome banks absorb part of these costs under zero-entry-cost packages.' },
        ]}
      />
    </div>
  );
}

const Block = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
    <div style={{ padding: '10px 14px 4px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--primary)' }}>{title}</div>
    {children}
  </div>
);

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '14px 16px', borderTop: '1px solid var(--border)', fontSize: 13, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ fontWeight: strong ? 600 : 400, color: strong ? 'var(--primary)' : 'var(--text)' }}>{label}</span>
    <b style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: strong ? 'var(--primary)' : 'var(--text)' }}>{value}</b>
  </div>
);
